
import { stockMovementRepository } from './repositories/stockMovementRepository';
import { stockLocationRepository } from './repositories/stockLocationRepository';
import { StockMovement, StockMovementType, StockLocationType } from '../types';
import { v4 as uuidv4 } from 'uuid';

export const stockLedgerService = {
    async getBalanceAt(locationId: string, stockItemId: string, asOfDate?: string): Promise<number> {
        // Note: IndexedDB is not optimized for "Sum where date < X".
        // For MVP, we fetch all movements for location and filter/sum in memory.
        // In multi-user, this is SQL SUM().
        const movements = await stockMovementRepository.findByLocation(locationId);

        const relevantMovements = movements.filter(m => {
            if (m.isVoid) return false;
            if (m.stockItemId !== stockItemId) return false;
            if (asOfDate && m.occurredAt > asOfDate) return false;
            return true;
        });

        let balance = 0;
        for (const m of relevantMovements) {
            // In our model:
            // StockMovement records might be single-entry focused (stockLocationId)
            // OR transfer focused (from/to).
            // We need to handle both if we support mixed models, but let's stick to our Transfer model:
            // A movement has `fromStockLocationId` and `toStockLocationId`.

            if (m.toStockLocationId === locationId) {
                // Income to this location
                balance += m.quantity;
            }
            if (m.fromStockLocationId === locationId) {
                // Expense from this location
                balance -= m.quantity;
            }

            // Legacy "Single Location" support (if we used stockLocationId for simple expense)
            if (m.stockLocationId === locationId) {
                if (m.type === 'INCOME' || m.type === 'ADJUSTMENT') {
                    // Adjustment logic depends on implementation. 
                    // Usually Adjustment is "Set to X" or "Add X". Let's assume Add X.
                    balance += m.quantity; // Positive qty = add, negative = remove?
                }
                if (m.type === 'EXPENSE') {
                    balance -= m.quantity;
                }
            }
        }
        return balance;
    },

    async createTransfer(
        params: {
            fromLocationId: string;
            toLocationId: string;
            stockItemId: string;
            quantity: number;
            documentType: string;
            documentId: string;
            externalRef: string;
            occurredAt?: string;
            createdByUserId?: string;
        }
    ): Promise<StockMovement> {
        // Idempotency check
        const existing = await stockMovementRepository.findByExternalRef(params.externalRef);
        if (existing) {
            if (existing.isVoid) {
                // Re-creating a voided transaction? Could be valid.
                // But usually externalRef is unique.
                // If reusing same ref, we might need a new ref or error.
                // For now, return existing to be safe.
                return existing;
            }
            return existing;
        }

        return stockMovementRepository.create({
            organizationId: 'current', // TODO: Get from context or params
            type: 'TRANSFER',
            stockItemId: params.stockItemId,
            quantity: params.quantity,
            fromStockLocationId: params.fromLocationId,
            toStockLocationId: params.toLocationId,
            documentType: params.documentType,
            documentId: params.documentId,
            externalRef: params.externalRef,
            occurredAt: params.occurredAt || new Date().toISOString(),
            isVoid: false,
            createdByUserId: params.createdByUserId
        });
    },

    async createExpense(
        params: {
            stockLocationId: string;
            stockItemId: string;
            quantity: number;
            documentType: string;
            documentId: string;
            externalRef: string;
            occurredAt?: string;
        }
    ): Promise<StockMovement> {
        const existing = await stockMovementRepository.findByExternalRef(params.externalRef);
        if (existing) return existing;

        return stockMovementRepository.create({
            organizationId: 'current',
            type: 'EXPENSE',
            stockItemId: params.stockItemId,
            quantity: params.quantity,
            fromStockLocationId: params.stockLocationId, // Expense comes FROM here
            // No 'to' location
            documentType: params.documentType,
            documentId: params.documentId,
            externalRef: params.externalRef,
            occurredAt: params.occurredAt || new Date().toISOString(),
            isVoid: false
        });
    },

    async createIncome(
        params: {
            stockLocationId: string;
            stockItemId: string;
            quantity: number;
            documentType: string;
            documentId: string;
            externalRef: string;
            occurredAt?: string;
        }
    ): Promise<StockMovement> {
        const existing = await stockMovementRepository.findByExternalRef(params.externalRef);
        if (existing) return existing;

        return stockMovementRepository.create({
            organizationId: 'current',
            type: 'INCOME',
            stockItemId: params.stockItemId,
            quantity: params.quantity,
            toStockLocationId: params.stockLocationId, // Income goes TO here
            // No 'from' location
            documentType: params.documentType,
            documentId: params.documentId,
            externalRef: params.externalRef,
            occurredAt: params.occurredAt || new Date().toISOString(),
            isVoid: false
        });
    },

    async voidByDocument(documentType: string, documentId: string, reason?: string): Promise<void> {
        const movements = await stockMovementRepository.findByDocument(documentType, documentId);
        for (const m of movements) {
            if (!m.isVoid) {
                m.isVoid = true;
                m.notes = m.notes ? `${m.notes} | Voided: ${reason}` : `Voided: ${reason}`;
                await stockMovementRepository.save(m);
            }
        }
    }
};
